import React from 'react';
import { motion } from 'framer-motion';
import { Finding, ReviewStatus } from '../../data/pillars';
import { fadeUp } from '../../utils/motion';
type FindingExampleProps = {
  pillarName: string;
  finding: Finding;
};
const statusClasses: Record<ReviewStatus, string> = {
  Reviewed: 'bg-teal/10 text-teal border-teal/30',
  'Awaiting review': 'bg-surface-muted text-navy border-line',
  'Insufficient evidence': 'bg-white text-ink-muted border-dashed border-ink-muted/50'
};
export function FindingExample({
  pillarName,
  finding
}: FindingExampleProps) {
  const rows: {
    label: string;
    value: string;
  }[] = [{
    label: 'Response excerpt',
    value: finding.excerpt
  }, {
    label: 'Observed reasoning',
    value: finding.observedReasoning
  }, {
    label: 'Rubric explanation',
    value: finding.rubric
  }, {
    label: 'Evidence limitations',
    value: finding.limitations
  }, {
    label: 'Development activity',
    value: finding.activity
  }, {
    label: 'Reflection',
    value: finding.reflection
  }];
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10" data-id="element-977" data-mp="3mlvywpjqmp9q">
      <motion.article {...fadeUp} aria-label="Illustrative finding" className="lg:col-span-8 bg-white border border-line rounded-lg overflow-hidden" data-id="element-978" data-mp="3hnc9gf5xqvcm">
        <header className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-line bg-surface-bg" data-id="element-979" data-mp="d4g04ejqlfm">
          <div data-id="element-980" data-mp="9fm2mjy45g35">
            <p className="text-xs font-medium text-ink-muted" data-id="element-981" data-mp="9fdrawhn2iu2">{pillarName}</p>
            <p className="font-heading font-semibold text-ink-dark" data-id="element-982" data-mp="4gsqauukbv4k">{finding.task}</p>
          </div>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-md border whitespace-nowrap ${statusClasses[finding.reviewStatus]}`} data-id="element-983" data-mp="j5fdfiqflr6c">
            {finding.reviewStatus}
          </span>
        </header>
        <dl className="divide-y divide-line" data-id="element-984" data-mp="s63pi7w9lhgl">
          {rows.map(row => <div key={row.label} className="grid grid-cols-1 sm:grid-cols-[11rem_1fr] gap-1 sm:gap-6 px-6 py-4" data-id="element-985" data-mp="o4rz405kgs7c">
              <dt className="text-sm font-medium text-ink-muted" data-id="element-986" data-mp="v88amld2hvpm">{row.label}</dt>
              <dd className="text-ink-dark leading-relaxed" data-id="element-987" data-mp="v659p6admi64">{row.value}</dd>
            </div>)}
          <div className="grid grid-cols-1 sm:grid-cols-[11rem_1fr] gap-1 sm:gap-6 px-6 py-4" data-id="element-988" data-mp="1565w513jleej">
            <dt className="text-sm font-medium text-ink-muted" data-id="element-989" data-mp="19ww14d823f8p">Review history</dt>
            <dd data-id="element-990" data-mp="19yz03mr6sipk">
              <ol className="space-y-1.5" data-id="element-991" data-mp="1dyhmkmynp1ui">
                {finding.history.map(entry => <li key={entry.date + entry.event} className="text-ink-dark" data-id="element-992" data-mp="1e0u3qxwlb86i">
                    <span className="text-sm text-ink-muted mr-3" data-id="element-993" data-mp="1iznfolw8ar4k">{entry.date}</span>
                    {entry.event}
                  </li>)}
              </ol>
            </dd>
          </div>
        </dl>
        <p className="px-6 py-3 border-t border-line bg-surface-bg text-xs text-ink-muted" data-id="element-994" data-mp="3po0e6mdv09au">
          Fictional example for illustration. Not a real person or a validated result.
        </p>
      </motion.article>

      <motion.aside {...fadeUp} aria-label="Insufficient evidence example" className="lg:col-span-4" data-id="element-995" data-mp="2brqqkqminmid">
        <div className="border border-dashed border-ink-muted/50 rounded-lg p-6 bg-white" data-id="element-996" data-mp="270qs3gxr0wdr">
          <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-md border ${statusClasses['Insufficient evidence']}`} data-id="element-997" data-mp="22zf9hvdalbf2">
            Insufficient evidence
          </span>
          <p className="mt-4 text-ink-dark leading-relaxed" data-id="element-998" data-mp="2uqutdhngsk1l">
            When a task does not give enough evidence to support an interpretation, the finding says so.
          </p>
          <p className="mt-3 text-ink-muted leading-relaxed" data-id="element-999" data-mp="2qn57izy6cgk9">
            Missing evidence is not the same as low performance. It is shown as its own state, and may lead to a
            further task rather than a development activity.
          </p>
        </div>
        <p className="mt-5 text-sm text-ink-muted leading-relaxed" data-id="element-1000" data-mp="1ac1cjx9lgfuy">
          Results are presented as a five-pillar profile. There is no overall intelligence score, civic rank or
          eligibility rating.
        </p>
      </motion.aside>
    </div>;
}